export interface ChannelSnoozePreset {
  label: string;
  key: string;
  getValue: () => Date;
}

/**
 * The next occurrence of 5:00 AM local time — the hard ceiling for any channel snooze.
 * A delivery channel can never be snoozed past the next 5 AM (business-day cutoff).
 * Before 5 AM today → today at 5 AM; otherwise → tomorrow at 5 AM.
 */
export function nextFiveAM(from: Date = new Date()): Date {
  const d = new Date(from);
  d.setHours(5, 0, 0, 0);
  if (d <= from) {
    d.setDate(d.getDate() + 1);
  }
  return d;
}

/** Clamp an expiry so it never lands past the next 5 AM ceiling. */
function capAtFiveAM(date: Date): Date {
  const ceiling = nextFiveAM();
  return date > ceiling ? ceiling : date;
}

const addMinutes = (mins: number) => () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() + mins);
  return capAtFiveAM(d);
};

const addHours = (hours: number) => () => {
  const d = new Date();
  d.setHours(d.getHours() + hours);
  return capAtFiveAM(d);
};

export const CHANNEL_SNOOZE_PRESETS: ChannelSnoozePreset[] = [
  { label: '15M', key: '15m', getValue: addMinutes(15) },
  { label: '30M', key: '30m', getValue: addMinutes(30) },
  { label: '45M', key: '45m', getValue: addMinutes(45) },
  { label: '1H', key: '1h', getValue: addHours(1) },
  { label: '2H', key: '2h', getValue: addHours(2) },
  { label: '4H', key: '4h', getValue: addHours(4) },
  { label: 'End of Day (5 AM)', key: 'eod', getValue: () => nextFiveAM() },
];

/**
 * Preset pre-selected when a disable/pause flow opens without an explicit
 * choice: 1 hour, matching the delivery-aggregator default. The single place
 * to change the default — every surface reads it from here.
 */
export const DEFAULT_SNOOZE_PRESET: ChannelSnoozePreset = CHANNEL_SNOOZE_PRESETS.find(
  (p) => p.key === '1h'
)!;
